import { safeGetSessionStorage } from '@/utils/safeStorage';

/**
 * Per-tab sessionStorage entries that hold user-visible data (saved filters)
 * must never survive an identity change: a tab can reach the sign-in screen
 * without the explicit `useLogout` cleanup — e.g. `useCheckAuthStatusQuery`
 * fails and `AuthGuard` redirects — leaving the previous advisor's snapshot
 * behind for whoever signs in next.
 *
 * The fix is to namespace the key with the signed-in identity, so a mismatched
 * entry is simply not found rather than restored.
 */

const SCOPE_SEPARATOR = '::';

/** Used before an identity is known; deliberately never matches a real user. */
const ANONYMOUS_SCOPE = 'anonymous';

/** Stable identity string for the signed-in user in their current role. */
export function userScopeId(
  user: string | null | undefined,
  role: string | null | undefined,
): string {
  const identity = user ?? ANONYMOUS_SCOPE;
  return role ? `${identity}|${role}` : identity;
}

/** True when the scope carries no signed-in identity yet. */
export function isAnonymousScope(scopeId: string): boolean {
  return (
    scopeId === ANONYMOUS_SCOPE || scopeId.startsWith(`${ANONYMOUS_SCOPE}|`)
  );
}

/** `baseKey` namespaced to one identity. */
export function userScopedKey(baseKey: string, scopeId: string): string {
  return `${baseKey}${SCOPE_SEPARATOR}${scopeId}`;
}

/**
 * Remove every sibling of `baseKey` that belongs to another identity, plus the
 * legacy unscoped `baseKey` written by earlier builds. Scoping alone already
 * makes those entries unreadable; this keeps another user's data from sitting in
 * the tab's storage after a re-login. Best-effort — storage may be disabled.
 */
export function pruneOtherUserScopes(baseKey: string, scopeId: string): void {
  const storage = safeGetSessionStorage();
  if (!storage) return;
  try {
    const prefix = `${baseKey}${SCOPE_SEPARATOR}`;
    const keep = userScopedKey(baseKey, scopeId);
    const stale: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key || key === keep) continue;
      if (key === baseKey || key.startsWith(prefix)) stale.push(key);
    }
    stale.forEach((key) => {
      storage.removeItem(key);
    });
  } catch {
    // storage disabled / mutation raced — pruning is best-effort.
  }
}
